import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Share2, Twitter, Copy, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const SocialSharePanel = ({ stats, achievements, onClose }) => {
  const [copied, setCopied] = useState(false);

  const unlockedAchievements = achievements || [];
  const unlockedCount = unlockedAchievements.length;

  const generateShareText = () => {
    const lines = [
      `I'm using NeuroWave One for brainwave entrainment!`,
      `${stats.totalSessions} sessions | ${stats.totalMinutes} min | ${stats.currentStreak}-day streak`,
    ];
    if (unlockedCount > 0) {
      lines.push(`${unlockedCount}/12 achievements unlocked`);
    }
    lines.push('');
    lines.push('Try it out:');
    lines.push(window.location.origin);
    return lines.join('\n');
  };

  const shareText = generateShareText();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Share text copied to clipboard.' });
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast({ title: 'Failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  const handleTwitter = () => {
    const text = encodeURIComponent(
      `I'm using NeuroWave One for brainwave entrainment! ${stats.totalSessions} sessions, ${stats.currentStreak}-day streak. ${unlockedCount}/12 achievements unlocked.`
    );
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="social-share-panel">
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border-white/20">
        <CardHeader className="relative">
          <Button onClick={onClose} variant="ghost" size="sm" className="absolute top-4 right-4 text-white hover:bg-white/10" data-testid="social-share-close-btn">
            <X className="w-5 h-5" />
          </Button>
          <CardTitle className="text-white text-xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-400" />
            Share Your Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview */}
          <div className="bg-black/30 rounded-lg p-4 border border-white/10">
            <pre className="text-white/80 text-xs whitespace-pre-wrap font-sans" data-testid="share-preview-text">{shareText}</pre>
          </div>

          {/* Share Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleTwitter}
              className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="share-twitter-btn"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Post on X
            </Button>
            <Button
              onClick={handleCopy}
              className={`flex-1 ${copied ? 'bg-green-600' : 'bg-white/10 hover:bg-white/20'} text-white`}
              data-testid="share-copy-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy Text'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialSharePanel;
